/**
 * D337: the world list's rows, kept apart from the sheets around them (`WorldList.tsx`) and from
 * the store, so the bench can walk them. D459: one card of ruled rows, the open world's with the
 * accent bar; a tap on an idle world's row opens that world's own sheet (open, rename, delete),
 * the old game's worlds open their summary, and Start a new world is the card's last row.
 */
import { getIdleContent } from '@hedgerow/data';
import { idleLandTraitNames } from '@hedgerow/sim';
import { Button } from '../ui/Button.tsx';
import { coins } from './notation.ts';
import { RowChevron, SETTINGS_LABEL } from './SettingsRows.tsx';
import { ThemeSwatch } from './ThemeSwatch.tsx';
import type { IdleWorldEntry } from './sync.ts';

/** D348: a world's theme by its own name, from its content: "Farm". */
export const themeName = (theme?: string): string => getIdleContent(theme).theme.name;

export const ROW = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  minHeight: 44,
  padding: '0 12px',
  borderRadius: 12,
  border: '1px solid var(--line)',
  color: 'var(--ink)',
  background: 'transparent',
  font: 'inherit',
  textAlign: 'left' as const,
};

export const SMALL = {
  margin: 0,
  font: '400 var(--fs-caption)/1.4 var(--font-ui)',
  color: 'var(--ink-2)',
};

/**
 * D337, D363: the words under the list when the player keeps as many worlds as they may.
 * "World" is the app's own word, not the theme's: the limit is on worlds, whatever each one
 * shows inside.
 */
export const worldLimitLine = (limit: number): string =>
  `You have ${limit} worlds, the most you can keep. Delete one to start another.`;

/** D344: a world's kind and land traits as one line: "Riverside, World pond, Wooded". */
export const traitLine = (ids: readonly string[], theme?: string): string =>
  idleLandTraitNames(getIdleContent(theme), ids).join(', ');

/** D337, D363: the button that runs the first run's naming and seed for another world. */
export const START_NEW_WORLD = 'Start a new world';

export interface WorldRowsProps {
  worlds: IdleWorldEntry[];
  /** How many idle worlds a player may keep (`worldsPerPlayer`). */
  limit: number;
  /** An idle world's row was tapped: show its own sheet. */
  onSelect: (world: IdleWorldEntry) => void;
  onView: (world: IdleWorldEntry) => void;
  onStart: () => void;
  /** A switch is out, so no row or button can be pressed twice. */
  busy?: boolean;
  /** Why the last switch did not happen, when it did not. */
  error?: string | null;
}

/** D459: a row of the card is one button, 12px in from the card's edge. */
const ROW_BUTTON = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  border: 0,
  background: 'transparent',
  color: 'var(--ink)',
  font: 'inherit',
  textAlign: 'left' as const,
  cursor: 'pointer',
};

/** A world's name, its theme and year, and its coins and rate; `full` adds seed and land. */
export function WorldDetails({
  f,
  full = false,
  named = true,
}: {
  f: IdleWorldEntry;
  full?: boolean;
  /** False when the name is already the sheet's title. */
  named?: boolean;
}) {
  return (
    <span style={{ display: 'grid', gap: 2, minWidth: 0, flex: 1 }}>
      {named && (
        <span
          title={f.name}
          style={{
            font: '600 var(--fs-name)/1.3 var(--font-ui)',
            overflowWrap: 'anywhere',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {f.name}
        </span>
      )}
      <span style={SMALL} data-testid={`world-theme-${f.worldId}`}>
        {themeName(f.theme)}
        {f.yearLine ? (
          <>
            {' · '}
            <span class="tnum">{f.yearLine}</span>
          </>
        ) : (
          f.year !== undefined && (
            <>
              {' · '}Year <span class="tnum">{f.year}</span>
            </>
          )
        )}
      </span>
      {f.money !== undefined && (
        <span style={SMALL} data-testid={`world-money-${f.worldId}`}>
          <span class="num" style={{ color: 'var(--ink)' }}>
            {coins(f.money)}
          </span>
          {f.rate !== undefined && f.rate > 0 && (
            <>
              {' · '}
              <span class="num">+{coins(f.rate)}/s</span>
            </>
          )}
        </span>
      )}
      {full && f.mapSeed && (
        <span style={SMALL} data-testid={`world-seed-${f.worldId}`}>
          Seed <span class="tnum">{f.mapSeed}</span>
        </span>
      )}
      {/* D344: the world's kind and what its land is like. */}
      {full && f.mapTraits?.length ? (
        <span style={SMALL} data-testid={`world-traits-${f.worldId}`}>
          {traitLine(f.mapTraits, f.theme)}
        </span>
      ) : null}
    </span>
  );
}

/**
 * D337: the list itself, without hooks, so the bench can walk it. The head carries the count
 * ("1 of 3") beside "Your worlds"; the card holds a row per world and Start a new world.
 */
export function WorldRows({
  worlds,
  limit,
  onSelect,
  onView,
  onStart,
  busy = false,
  error = null,
}: WorldRowsProps) {
  const playable = worlds.filter((f) => !f.archived).length;
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h3
          style={{
            margin: '6px 2px -2px',
            font: '600 var(--fs-caption)/1.2 var(--font-ui)',
            color: 'var(--ink-2)',
          }}
        >
          Your worlds
        </h3>
        {playable > 0 && (
          <span style={{ ...SMALL, margin: '6px 2px -2px' }} data-testid="world-count">
            <span class="tnum">{playable}</span> of <span class="tnum">{limit}</span>
          </span>
        )}
      </div>
      <div class="idle-set-group" data-testid="world-list">
        {worlds.map((f) =>
          f.archived ? (
            <button
              key={f.worldId}
              type="button"
              data-testid={`world-${f.worldId}`}
              onClick={() => onView(f)}
              style={ROW_BUTTON}
            >
              <span style={{ ...SETTINGS_LABEL, flex: 1 }}>{f.name}</span>
              <span
                style={{ font: '500 var(--fs-caption)/1 var(--font-ui)', color: 'var(--ink-2)' }}
              >
                Archived
              </span>
              <RowChevron />
            </button>
          ) : (
            <button
              key={f.worldId}
              type="button"
              data-testid={`world-${f.worldId}`}
              aria-current={f.current ? 'true' : undefined}
              disabled={busy}
              onClick={() => onSelect(f)}
              // D459: the one open is marked by a 3px accent bar on its left edge (the padding
              // gives back the two extra pixels, so nothing shifts).
              style={{
                ...ROW_BUTTON,
                padding: f.current ? '10px 12px 10px 9px' : '10px 12px',
                borderLeft: f.current ? '3px solid var(--accent)' : undefined,
              }}
            >
              <ThemeSwatch theme={f.theme} size={44} icon={22} />
              <WorldDetails f={f} />
              <RowChevron />
            </button>
          ),
        )}
        {/* Nothing listed means the list has not loaded: the open world is always in it. */}
        {playable === 0 ? null : playable < limit ? (
          <Button
            full
            variant="ghost"
            data-testid="start-new-world"
            disabled={busy}
            onClick={onStart}
            style={{
              height: 44,
              width: 'calc(100% - 24px)',
              margin: '8px 12px',
              color: 'var(--accent)',
              border: '1px solid var(--accent)',
            }}
          >
            {START_NEW_WORLD}
          </Button>
        ) : (
          <p style={{ ...SMALL, display: 'flex', alignItems: 'center' }} data-testid="world-limit">
            {worldLimitLine(limit)}
          </p>
        )}
      </div>
      {error && (
        <p style={{ ...SMALL, color: 'var(--red)' }} role="alert">
          {error}
        </p>
      )}
      {worlds.some((f) => f.archived) && (
        <p style={SMALL}>Archived worlds stay until you delete them.</p>
      )}
    </>
  );
}
